/**
 * Real CRS alignment preview client.
 *
 * Replaces `alignPreviewMock.previewAlignment` with a live call to
 * `POST /api/v1/gtwins/{gtwinId}/align/preview`.
 *
 * Falls back to an Identity directive when the endpoint is unreachable
 * so the drop pipeline never hard-fails on a network error alone.
 */

import { buildOptions } from './wizardOptions';
import type { CrsReconciliation } from './wizardTypes';

// ---------------------------------------------------------------------------
// Result type (re-exported for convenience)
// ---------------------------------------------------------------------------

export type AlignPreviewResult = CrsReconciliation;

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const PLATFORM_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GTWIN_API_URL) ||
  'http://localhost:3001';

// ---------------------------------------------------------------------------
// Identity fallback — used when the endpoint is unavailable.
// ---------------------------------------------------------------------------

function identityFallback(): CrsReconciliation {
  const base: Omit<CrsReconciliation, 'options'> = {
    directive: 'identity',
    source: { crs: '', coordinateMode: 'unknown-local' },
    target: { kind: 'unknown' },
    recommendation: 'align-on-read',
    reason: 'Align-preview endpoint unreachable; defaulting to Identity.',
  };
  return { ...base, options: buildOptions({ ...base, options: [] }) };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Call the platform's alignment preview endpoint and return a fully
 * populated `CrsReconciliation` triage (including option cards).
 *
 * @param gtwinId - The gTwin ID to preview against.
 * @param item    - The scene item (or partial) being dropped.
 * @param slot    - Optional scene slot override (defaults to `"realityData"`).
 */
export async function previewAlignment(
  gtwinId: string,
  item: Record<string, unknown>,
  slot: string = 'realityData',
): Promise<AlignPreviewResult> {
  const url = `${PLATFORM_BASE.replace(/\/+$/, '')}/api/v1/gtwins/${encodeURIComponent(gtwinId)}/align/preview`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ item, slot }),
    });
  } catch {
    return identityFallback();
  }

  if (!res.ok) {
    return identityFallback();
  }

  const body = (await res.json()) as Partial<CrsReconciliation> & {
    targetCrs?: string;
    code?: string;
  };

  const triage: Omit<CrsReconciliation, 'options'> = {
    directive: body.directive ?? 'identity',
    source: body.source ?? { crs: '', coordinateMode: 'unknown-local' },
    target: body.target ?? { kind: 'unknown' },
    recommendation: body.recommendation ?? 'align-on-read',
    reason: body.reason,
    errorCode: body.errorCode,
  };

  return { ...triage, options: buildOptions({ ...triage, options: [] }) };
}
